import { Box, TextLink } from '@palmetto/palmetto-components';

import { AccountStipulationReason } from 'types';

import { AccountMilestoneStipulation } from '../../types/Accounts';
import { HiddenLightReachInfoForEpcUser } from '../auth/HiddenLightReachInfoToEpcUser';
import DateTimeDisplay from '../DateTime';

interface AdminStipulationDetailsProps {
  stipulation: AccountMilestoneStipulation;
  forBanner?: boolean;
}

const CustomerActionRequiredDescription = () => {
  return (
    <Box as="em" fontSize="sm">
      <p>
        Customer Task: Customer must complete an in-depth review of key contract details. This task builds on the
        Clear Facts Disclosure, and is designed to affirm key details of their contract. An installer reference guide
        is available via the installer knowledge base.
      </p>
      <br />
      <p>Customer Steps: </p>
      <p>
        1) Customer logs in to their{' '}
        <TextLink href="https://home.palmetto.com" target="_blank" rel="noreferrer">
          home.palmetto.com
        </TextLink>{' '}
        account
      </p>
      <p>{'2) Review tasks and confirm details by checking the boxes, and electronically signing'}</p>
      <p>{'3) Click SUBMIT'}</p>
      <p>{'4) This stipulation will clear automatically upon submission'}</p>
      <br />
      <p>
        Installer reference guide:{' '}
        <TextLink
          href="https://help.palmetto.finance/en/articles/8284546-4-completing-ntp-m0-requirements"
          target="_blank"
          rel="noreferrer"
        >
          https://help.palmetto.finance/en/articles/8284546-4-completing-ntp-m0-requirements
        </TextLink>{' '}
      </p>
    </Box>
  );
};

const WelcomeCallStipulationDescription = () => {
  return (
    <Box as="em" fontSize="sm">
      <p>
        <strong>Standard Consumer Protection Task:</strong> Customer must complete Option [1] and/or [2] to clear
        stipulation.
      </p>
      <br />
      <p>
        <strong>Option 1: PLR VoiceVerify Call (Recommended for fastest approval).</strong> Customer completes a
        recorded call w/ AI agent via their{' '}
        <TextLink href="https://home.palmetto.com" target="_blank" rel="noreferrer">
          home.palmetto.com
        </TextLink>{' '}
        account. Stipulation clears automatically upon successful completion.
      </p>
      <br />
      <p>
        <strong>Option 2: Installer Welcome Call (Manual review).</strong> Installer submits a copy of their own
        welcome call to PLR underwriting support. Calls must reasonably affirm{' '}
        <TextLink
          href="https://help.palmetto.finance/en/articles/8284326-compliance-guidelines"
          target="_blank"
          rel="noreferrer"
        >
          all minimum criteria
        </TextLink>
        . Please contact underwriting via Intercom Chat to request manual review.
      </p>
    </Box>
  );
};

const AdminStipulationDetails = ({ stipulation, forBanner }: AdminStipulationDetailsProps) => {
  const { description, reason, appliedBy, appliedByDisplayName, appliedDate } = stipulation || {};
  const isCustomerActionRequired = reason === AccountStipulationReason.customerActionRequired;
  const isWelcomeCall = reason === AccountStipulationReason.welcomeCall;

  const reasonText = isCustomerActionRequired ? 'Customer Action Required' : reason;
  const isPotentialCustomerActionRequiredStipulationAddedByAutomation =
    isCustomerActionRequired && appliedBy === 'unknown';
  const appliedByText = isPotentialCustomerActionRequiredStipulationAddedByAutomation
    ? 'LightReach Support'
    : appliedByDisplayName || appliedBy;

  return (
    <Box
      flex="auto"
      gap="xs"
      padding={forBanner ? 'lg' : '0'}
      fontSize="sm"
      fontWeight="medium"
      justifyContent="center"
    >
      <Box direction="row" gap="2xs">
        {reason && (
          <>
            <Box fontWeight="bold">{reasonText}</Box>
            {' - '}
          </>
        )}
        {isWelcomeCall ? (
          <WelcomeCallStipulationDescription />
        ) : isPotentialCustomerActionRequiredStipulationAddedByAutomation ? (
          <CustomerActionRequiredDescription />
        ) : (
          <Box as="em">{description}</Box>
        )}
      </Box>

      {appliedDate && (
        <Box direction="row" gap="2xs" fontSize="xs">
          <DateTimeDisplay value={appliedDate?.toString()} fontSize="xs" dateFormat="DD h:mm a" inline /> by{' '}
          <HiddenLightReachInfoForEpcUser fallbackChildren="LightReach Support">
            {appliedByText}
          </HiddenLightReachInfoForEpcUser>
        </Box>
      )}
    </Box>
  );
};

export default AdminStipulationDetails;
